import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { RefObject } from 'react';
import type { TagResponse } from '../api';
import { Button } from '../components/ui';
import { tagColors } from './tagColors';

/** Wide enough for nine swatches on two rows; wider than the sidebar it hangs off. */
const width = 240;
const margin = 8;

interface TagEditorProps {
  tag: TagResponse;
  busy?: boolean;
  error?: string;
  /**
   * The row this hangs from. It is both the thing the popover is positioned against and what
   * counts as "inside" for dismissal — the latter because it holds the button that opened the
   * editor, and without it pointerdown closes the editor a moment before the click reopens it,
   * so the toggle never appears to work.
   */
  anchorRef: RefObject<HTMLElement | null>;
  /** Both fields, always: the API takes a complete tag, not a patch. */
  onSave: (next: { name: string; color: string | null }) => void;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * Rename, recolour or delete one tag, in a popover hanging off its sidebar row.
 *
 * Rendered into the body rather than beside the row. The sidebar is `overflow-y-auto`, and a box
 * with `overflow-y: auto` computes `overflow-x` to `auto` as well — so a popover wider than the
 * column cannot escape it and instead gives the whole sidebar a horizontal scrollbar. Fixed
 * positioning against the row's rect sidesteps that entirely, at the cost of having to follow the
 * row when anything scrolls.
 *
 * Presentational on purpose — the sidebar owns the mutations — so this can be tested by clicking
 * it rather than by standing up a query client.
 */
export function TagEditor({
  tag,
  busy = false,
  error,
  anchorRef,
  onSave,
  onDelete,
  onClose,
}: TagEditorProps) {
  const [name, setName] = useState(tag.name);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    function place() {
      const rect = anchor!.getBoundingClientRect();

      setPosition({
        top: rect.bottom + 4,
        // Clamped, so a row near the right edge does not push the popover off-screen.
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
      });
    }

    place();

    // Capture, so scrolling the sidebar itself counts and not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef]);

  useEffect(() => {
    // Selected rather than merely focused: renaming is the likeliest reason to open this, and
    // typing over the old name should not need a Ctrl+A first.
    nameRef.current?.select();
  }, []);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;

      // The popover is no longer inside the row, so both count as inside.
      if (containerRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }

      onClose();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-label={`Edit tag ${tag.name}`}
      style={{ top: position.top, left: position.left, width }}
      className="fixed z-50 flex flex-col gap-3 rounded-md border border-border-subtle
        bg-surface-raised p-3 shadow-lg"
    >
      <form
        className="flex flex-col gap-1.5"
        onSubmit={(event) => {
          event.preventDefault();

          const trimmed = name.trim();
          if (!trimmed || trimmed === tag.name) {
            onClose();
            return;
          }

          onSave({ name: trimmed, color: tag.color });
        }}
      >
        <input
          ref={nameRef}
          aria-label="Tag name"
          value={name}
          disabled={busy}
          // Matches Tag.MaxNameLength, so the field stops before the server has to.
          maxLength={50}
          onChange={(event) => setName(event.target.value)}
          className="w-full rounded-md border border-border-subtle bg-surface px-2 py-1 text-xs
            text-text disabled:opacity-50"
        />
      </form>

      <div className="flex flex-col gap-1.5">
        <span id={`${tag.id}-colour`} className="text-xs text-text-muted">
          Colour
        </span>

        <div className="flex flex-wrap gap-1.5" aria-labelledby={`${tag.id}-colour`}>
          {tagColors.map((colour) => (
            <button
              key={colour.value}
              type="button"
              disabled={busy}
              aria-label={colour.name}
              aria-pressed={tag.color === colour.value}
              // Recolouring keeps the name it currently has, so the two controls stay independent
              // even though one request carries both.
              onClick={() => onSave({ name: tag.name, color: colour.value })}
              className={`size-5 rounded-full border-2 disabled:opacity-50
                ${tag.color === colour.value ? 'border-text' : 'border-transparent'}`}
              style={{ backgroundColor: colour.value }}
            />
          ))}

          <button
            type="button"
            disabled={busy}
            aria-label="No colour"
            aria-pressed={tag.color === null}
            onClick={() => onSave({ name: tag.name, color: null })}
            className={`size-5 rounded-full border-2 text-[10px] leading-none text-text-faint
              disabled:opacity-50
              ${tag.color === null ? 'border-text' : 'border-border-strong'}`}
          >
            ×
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}

      {confirmingDelete ? (
        <div className="flex flex-col gap-1.5 border-t border-border-subtle pt-2">
          <p className="text-xs text-text-muted">
            Delete {tag.name}? It comes off {tag.snippetCount}{' '}
            {tag.snippetCount === 1 ? 'snippet' : 'snippets'}. The snippets stay.
          </p>
          <div className="flex gap-1.5">
            <Button variant="danger" disabled={busy} onClick={onDelete} className="px-2 py-1 text-xs">
              {busy ? 'Deleting…' : 'Delete'}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => setConfirmingDelete(false)}
              className="px-2 py-1 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="border-t border-border-subtle pt-2">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => setConfirmingDelete(true)}
            className="px-2 py-1 text-xs"
          >
            Delete tag
          </Button>
        </div>
      )}
    </div>,
    document.body,
  );
}
